/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebase";
import { tryResolveTenantId } from "../utils/tenant";
import { useLocations } from "../hooks/useFirestoreData";
import { resolveLocationTimezone } from "../utils/shiftTimezone";
import { getBusinessDateKey } from "../utils/businessDateKey";

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const [hRaw, mRaw] = timeStr.split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

const expandRange = (start, end) => {
  if (start == null || end == null) return null;
  return end <= start ? { start, end: end + 1440 } : { start, end };
};

const rangesOverlap = (a, b) => Math.max(a.start, b.start) < Math.min(a.end, b.end);

const sumNumericLeaves = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!value || typeof value !== "object") return 0;
  return Object.values(value).reduce((sum, child) => sum + sumNumericLeaves(child), 0);
};

const resolveDaypartTargetCount = (targets, dayKey, daypartId, useSameEveryDay) => {
  if (!targets || !daypartId) return 0;
  const dayBucket = targets[dayKey] || null;
  const allBucket = targets.ALL || null;
  const dayNode = dayBucket ? dayBucket[daypartId] : undefined;
  const allNode = allBucket ? allBucket[daypartId] : undefined;
  // Respect explicit mode first so stale keys from prior edits do not override intent.
  if (useSameEveryDay === true) return typeof allNode !== "undefined" ? sumNumericLeaves(allNode) : 0;
  if (useSameEveryDay === false) {
    if (typeof dayNode !== "undefined") return sumNumericLeaves(dayNode);
    // In per-day mode, do not fall back to ALL.
    return 0;
  }
  if (typeof dayNode !== "undefined") return sumNumericLeaves(dayNode);
  if (typeof allNode !== "undefined") return sumNumericLeaves(allNode);
  return 0;
};

const formatDisplayTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return "--:--";
  const dt = DateTime.fromFormat(timeStr, "HH:mm");
  return dt.isValid ? dt.toFormat("h:mm a") : timeStr;
};

const burnPercent = (projectedHours, thresholdHours) => {
  const projected = Number(projectedHours || 0);
  const threshold = Number(thresholdHours || 0);
  if (!Number.isFinite(projected) || !Number.isFinite(threshold) || threshold <= 0) return 0;
  return Math.max(0, Math.min(140, (projected / threshold) * 100));
};

const resolveRiskIcon = (name) => {
  const label = String(name || "").toLowerCase();
  if (label.includes("dinner")) return "🍽";
  if (label.includes("lunch") || label.includes("peak")) return "☀";
  if (label.includes("breakfast")) return "🥐";
  return "⚠";
};

const resolveOvertimeHitLabel = (row) => {
  const explicitHitLabel =
    row?.projectedOvertimeAt ||
    row?.projectedHitTime ||
    row?.overtimeHitTime ||
    row?.crossingTimeLabel ||
    row?.crossingLabel;
  if (explicitHitLabel) return `Will hit overtime: ${explicitHitLabel}`;

  const hoursToThreshold = Number(row?.hoursToThreshold || 0);
  if (Number.isFinite(hoursToThreshold) && hoursToThreshold > 0) {
    return `Will hit overtime in ~${hoursToThreshold.toFixed(1)} hours`;
  }
  if (Number(row?.overtimeRiskHours || 0) > 0) return "Overtime threshold already exceeded";
  return "Overtime threshold approaching";
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const resolveCoverageSeverity = (coveragePct, safeThresholdPct, warningBandPct) => {
  const coverage = Number(coveragePct || 0);
  const safeThreshold = Number(safeThresholdPct || 80);
  const warningBand = Number(warningBandPct || 10);
  const yellowFloor = safeThreshold - warningBand;

  if (coverage >= safeThreshold) return "safe";
  if (coverage >= yellowFloor) return "strain";
  return "critical";
};

const compactWindowLabel = (windowLabel) => {
  const [startRaw, endRaw] = String(windowLabel || "").split(" - ").map((v) => String(v || "").trim());
  if (!startRaw || !endRaw) return "";
  const compact = (part) =>
    part
      .toLowerCase()
      .replace(":00", "")
      .replace(/\s/g, "");
  return `${compact(startRaw)}-${compact(endRaw)}`;
};

const formatWeekRangeLabel = (startDateKey, endDateKey) => {
  const start = DateTime.fromISO(String(startDateKey || ""));
  const end = DateTime.fromISO(String(endDateKey || ""));
  if (!start.isValid || !end.isValid) return "This Week";

  if (start.year === end.year && start.month === end.month) {
    return `${start.toFormat("LLL")} ${start.day}\u2013${end.day}`;
  }
  if (start.year === end.year) {
    return `${start.toFormat("LLL")} ${start.day}\u2013${end.toFormat("LLL")} ${end.day}`;
  }
  return `${start.toFormat("LLL")} ${start.day}, ${start.year}\u2013${end.toFormat("LLL")} ${end.day}, ${end.year}`;
};

const formatClockTimeLabel = (dateValue, zone) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return "--:--";
  return DateTime.fromJSDate(dateValue).setZone(zone).toFormat("h:mm a");
};

export default function Dashboard() {
  const [tenantId, setTenantId] = useState(null);
  const [userPermission, setUserPermission] = useState("manager");
  const [managerLocationId, setManagerLocationId] = useState("");
  const [dateMode, setDateMode] = useState("today");
  const [selectedLocation, setSelectedLocation] = useState("");
  const locations = useLocations(selectedLocation, setSelectedLocation);

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardDataByQuery, setDashboardDataByQuery] = useState(null);
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [otRiskData, setOtRiskData] = useState(null);
  const [autoClockOutRows, setAutoClockOutRows] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [riskMeta, setRiskMeta] = useState({ enabled: false, hasTargets: false });

  const isOwner = userPermission === "owner";
  const selectedLocationObj =
    selectedLocation === "all" ? null : locations.find((loc) => loc.id === selectedLocation);
  const locationTimeZone = selectedLocationObj
    ? resolveLocationTimezone(selectedLocationObj)
    : DateTime.local().zoneName;

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  const { currentDateKey, activeDateKeys } = useMemo(() => {
    const nowUtc = new Date(nowMs);
    const todayKey = selectedLocationObj
      ? getBusinessDateKey(selectedLocationObj, nowUtc)
      : DateTime.fromJSDate(nowUtc).setZone(locationTimeZone).toISODate();
    if (dateMode !== "week") return { currentDateKey: todayKey, activeDateKeys: [todayKey] };

    const weekStart = DateTime.fromISO(todayKey, { zone: locationTimeZone }).startOf("week");
    const weekKeys = Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i }).toISODate());
    return { currentDateKey: todayKey, activeDateKeys: weekKeys };
  }, [selectedLocationObj, locationTimeZone, dateMode, nowMs]);
  const activeDateKeySig = activeDateKeys.join("|");
  const businessDayLabel = useMemo(() => {
    if (!selectedLocationObj || !currentDateKey) return "";

    const weekday = DateTime.fromISO(currentDateKey, { zone: locationTimeZone }).toFormat("cccc");

    const cutoff = selectedLocationObj.businessDayCutoff;
    let cutoffHour = 24;
    let cutoffMinute = 0;

    if (cutoff && typeof cutoff === "object") {
      const h = Number(cutoff.hour);
      const m = Number(cutoff.minute);
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        cutoffHour = h;
        cutoffMinute = m;
      }
    } else if (typeof selectedLocationObj.endOfBusiness === "string") {
      const [hRaw, mRaw] = selectedLocationObj.endOfBusiness.split(":");
      const h = Number(hRaw);
      const m = Number(mRaw);
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        cutoffHour = h;
        cutoffMinute = m;
      }
    }

    const cutoffText =
      cutoffHour === 24 && cutoffMinute === 0
        ? "12:00 AM"
        : DateTime.fromObject(
            { hour: Math.max(0, Math.min(23, cutoffHour)), minute: Math.max(0, Math.min(59, cutoffMinute)) },
            { zone: locationTimeZone }
          ).toFormat("h:mm a");

    return `Business Day: ${weekday} (Closes at ${cutoffText})`;
  }, [selectedLocationObj, currentDateKey, locationTimeZone]);

  const formatLastUpdated = (value) => {
    if (!value) return "Not yet";
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "Not yet";
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  // Resolve tenant
  useEffect(() => {
    async function resolveTenant() {
      const id = await tryResolveTenantId();
      setTenantId(id);
    }
    resolveTenant();
  }, []);

  // Resolve permission and manager default location from current admin profile
  useEffect(() => {
    let mounted = true;

    async function resolveUserContext() {
      try {
        const raw = localStorage.getItem("adminUser");
        const parsed = raw ? JSON.parse(raw) : null;
        const perm = (parsed?.permissionLevel || "manager").toString().toLowerCase();
        if (!mounted) return;
        setUserPermission(perm);

        if (!tenantId || !parsed?.id || perm === "owner") return;
        const empRef = doc(db, `tenants/${tenantId}/employees`, parsed.id);
        const empSnap = await getDoc(empRef);
        if (!mounted || !empSnap.exists()) return;

        const emp = empSnap.data() || {};
        const assignmentLocs = Object.keys(emp.assignments || {});
        const defaultLoc = emp.locationId || assignmentLocs[0] || "";
        if (defaultLoc) setManagerLocationId(defaultLoc);
      } catch (err) {
        console.warn("Dashboard user context resolution failed:", err);
      }
    }

    resolveUserContext();
    return () => {
      mounted = false;
    };
  }, [tenantId]);

  // Role-based location auto-adjust
  useEffect(() => {
    if (!locations.length) return;

    if (isOwner) {
      if (!selectedLocation || selectedLocation === "all") {
        setSelectedLocation(locations[0].id);
      }
      return;
    }

    const managerLocExists = managerLocationId && locations.some((l) => l.id === managerLocationId);
    if (managerLocExists) {
      if (selectedLocation !== managerLocationId) setSelectedLocation(managerLocationId);
      return;
    }

    if (!selectedLocation || selectedLocation === "all") {
      setSelectedLocation(locations[0].id);
    }
  }, [isOwner, locations, managerLocationId, selectedLocation]);

  // Subscribe to dashboard doc
  useEffect(() => {
    if (
      !tenantId ||
      !selectedLocation ||
      selectedLocation === "all" ||
      dateMode !== "today"
    ) return;

    const ref = doc(
      db,
      `tenants/${tenantId}/dashboard/today_${selectedLocation}`
    );

    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setDashboardData(snap.data());
        setLastUpdatedAt(snap.data()?.updatedAt || null);
      } else {
        setDashboardData(null);
      }
    });

    return () => unsub();
  }, [tenantId, selectedLocation, dateMode]);

  // Fallback: resolve dashboard row by fields (locationId + localDateKey) in case doc-id convention differs
  useEffect(() => {
    if (!tenantId || !selectedLocation || selectedLocation === "all" || dateMode !== "today") {
      setDashboardDataByQuery(null);
      return;
    }

    const q = query(
      collection(db, `tenants/${tenantId}/dashboard`),
      where("locationId", "==", selectedLocation),
      where("localDateKey", "==", currentDateKey)
    );

    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const row = snap.docs[0].data();
        setDashboardDataByQuery(row);
        if (!dashboardData?.updatedAt && row?.updatedAt) setLastUpdatedAt(row.updatedAt);
      } else {
        setDashboardDataByQuery(null);
      }
    });

    return () => unsub();
  }, [tenantId, selectedLocation, dateMode, currentDateKey, dashboardData?.updatedAt]);

  // Overtime risk snapshot for selected location/week
  useEffect(() => {
    if (!tenantId || !selectedLocation || selectedLocation === "all" || dateMode !== "today") {
      setOtRiskData(null);
      return;
    }

    const ref = doc(db, `tenants/${tenantId}/dashboard/otRisk_${selectedLocation}`);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setOtRiskData(null);
        return;
      }
      setOtRiskData(snap.data());
    });
    return () => unsub();
  }, [tenantId, selectedLocation, dateMode]);

  // Auto clock-outs over trailing 7 business days for selected location
  useEffect(() => {
    if (!tenantId || !selectedLocation || selectedLocation === "all" || dateMode !== "today") {
      setAutoClockOutRows([]);
      return;
    }

    const localNow = DateTime.now().setZone(locationTimeZone).startOf("day");
    const trailingDateKeys = Array.from({ length: 7 }, (_, idx) => localNow.minus({ days: idx }).toISODate());

    const entriesById = new Map();
    const legacyEntriesById = new Map();

    const recomputeAutoClockOuts = () => {
      const combined = new Map([...entriesById, ...legacyEntriesById]);
      const rows = Array.from(combined.values())
        .filter((entry) => {
          if (!entry || entry.status === "voided") return false;
          if (String(entry.type || "").toLowerCase() !== "clock_out") return false;
          return (
            entry.isAutoClockOut === true ||
            String(entry.source || "").toLowerCase() === "system" ||
            !!entry.autoClockOutReason
          );
        })
        .map((entry) => {
          const ts = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp || 0);
          return {
            employeeId: String(entry.employeeId || ""),
            employeeName: entry.employeeName || String(entry.employeeId || "Unknown"),
            clockOutAt: ts,
            reason: entry.autoClockOutReason || "",
          };
        })
        .filter((row) => row.employeeId && row.clockOutAt instanceof Date && !Number.isNaN(row.clockOutAt.getTime()))
        .sort((a, b) => (b.clockOutAt?.getTime?.() || 0) - (a.clockOutAt?.getTime?.() || 0))
        .map((row) => ({
          employeeId: row.employeeId,
          employeeName: row.employeeName || row.employeeId,
          clockOutAt: row.clockOutAt,
          reason: row.reason,
        }));

      setAutoClockOutRows(rows);
    };

    const baseRef = collection(db, `tenants/${tenantId}/timeEntries`);
    const entriesQ = query(
      baseRef,
      where("locationId", "==", selectedLocation),
      where("businessDateKey", "in", trailingDateKeys)
    );
    const legacyEntriesQ = query(
      baseRef,
      where("locationId", "==", selectedLocation),
      where("date", "in", trailingDateKeys)
    );

    const unsubEntries = onSnapshot(entriesQ, (snap) => {
      entriesById.clear();
      snap.docs.forEach((d) => entriesById.set(d.id, d.data()));
      recomputeAutoClockOuts();
    });
    const unsubLegacyEntries = onSnapshot(legacyEntriesQ, (snap) => {
      legacyEntriesById.clear();
      snap.docs.forEach((d) => legacyEntriesById.set(d.id, d.data()));
      recomputeAutoClockOuts();
    });

    return () => {
      unsubEntries();
      unsubLegacyEntries();
    };
  }, [tenantId, selectedLocation, dateMode, locationTimeZone]);

  // Recompute snapshot metrics from shifts + timecards + time entries in realtime
  useEffect(() => {
    if (!tenantId || !selectedLocation || !activeDateKeys.length) return;

    const shiftsRef = query(
      collection(db, `tenants/${tenantId}/shifts`),
      activeDateKeys.length === 1
        ? where("localDateKey", "==", activeDateKeys[0])
        : where("localDateKey", "in", activeDateKeys)
    );

    let latestShifts = [];
    let latestEntries = [];
    let latestTimecards = [];

    const computeMetrics = () => {
      let scheduledMinutes = 0;
      let workedMinutes = 0;
      let openShifts = 0;
      let lateCount = 0;
      let earlyOutCount = 0;
      let clockedInCount = 0;
      const timecardByShiftId = new Map();
      latestTimecards.forEach((timecard) => {
        if (!timecard?.shiftId) return;
        timecardByShiftId.set(String(timecard.shiftId), timecard);
      });

      const lastByEmployee = new Map();
      latestEntries.forEach((entry) => {
        const entryType = String(entry.type || "").toLowerCase();
        if (!entry.employeeId || !entryType) return;
        const ts = entry.timestamp?.toDate
          ? entry.timestamp.toDate()
          : new Date(entry.timestamp || 0);
        const existing = lastByEmployee.get(entry.employeeId);
        if (!existing || ts > existing.ts) {
          lastByEmployee.set(entry.employeeId, { ts, type: entryType });
        }
      });
      lastByEmployee.forEach((v) => {
        if (v.type === "clock_in" || v.type === "break_start" || v.type === "break_end") {
          clockedInCount++;
        }
      });

      latestShifts.forEach((shift) => {
        if (selectedLocation !== "all" && shift.locationId !== selectedLocation) return;
        if (shift.deleted === true) return;
        const timecard = timecardByShiftId.get(String(shift.id));
        const attendanceSource = timecard || shift;

        if (attendanceSource.wasTardy === true || Number(attendanceSource.tardyMinutes || 0) > 0) lateCount++;
        if (attendanceSource.leftEarly === true || attendanceSource.wasLeftEarly === true || Number(attendanceSource.earlyOutMinutes || 0) > 0) earlyOutCount++;
        if (!shift.employeeId || shift.isOpenShift === true) openShifts++;

        if (!shift.shiftStartTime || !shift.shiftEndTime) return;
        const [startH, startM] = String(shift.shiftStartTime).split(":").map(Number);
        const [endH, endM] = String(shift.shiftEndTime).split(":").map(Number);
        const startTotal = startH * 60 + startM;
        const endTotal = endH * 60 + endM;
        if (!Number.isNaN(startTotal) && !Number.isNaN(endTotal)) {
          const shiftMinutes = Math.max(0, endTotal - startTotal);
          scheduledMinutes += shiftMinutes;
          workedMinutes += Math.max(0, Number((timecard && timecard.workedMinutes) || shift.workedMinutes || 0));
        }
      });

      setLiveMetrics({
        locationId: selectedLocation,
        localDateKey: activeDateKeys[0],
        scheduledHours: scheduledMinutes / 60,
        openShifts,
        lateCount,
        earlyOutCount,
        clockedInCount,
        mode: dateMode,
      });
      setLastUpdatedAt(new Date());
    };

    const unsubShifts = onSnapshot(shiftsRef, (snap) => {
      latestShifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      computeMetrics();
    });

    const entriesRef = query(
      collection(db, `tenants/${tenantId}/timeEntries`),
      activeDateKeys.length === 1
        ? where("businessDateKey", "==", activeDateKeys[0])
        : where("businessDateKey", "in", activeDateKeys)
    );
    const unsubEntries = onSnapshot(entriesRef, (snap) => {
      latestEntries = snap.docs
        .map((d) => d.data())
        .filter((entry) =>
          selectedLocation === "all" ? true : entry.locationId === selectedLocation
        );
      computeMetrics();
    });
    const timecardsRef = query(
      collection(db, `tenants/${tenantId}/timecards`),
      activeDateKeys.length === 1
        ? where("businessDateKey", "==", activeDateKeys[0])
        : where("businessDateKey", "in", activeDateKeys)
    );
    const unsubTimecards = onSnapshot(timecardsRef, (snap) => {
      latestTimecards = snap.docs
        .map((d) => d.data())
        .filter((timecard) =>
          selectedLocation === "all" ? true : timecard.locationId === selectedLocation
        );
      computeMetrics();
    });

    return () => {
      unsubShifts();
      unsubEntries();
      unsubTimecards();
    };
  }, [tenantId, selectedLocation, activeDateKeySig, activeDateKeys, dateMode]);

  // Risk alerts: staffing targets vs scheduled/live headcount by daypart
  useEffect(() => {
    if (!tenantId || !selectedLocation || selectedLocation === "all" || dateMode !== "today" || !currentDateKey) {
      setRiskAlerts([]);
      setRiskMeta({ enabled: false, hasTargets: false });
      return;
    }

    const staffingRef = doc(db, `tenants/${tenantId}/staffingTargets/${selectedLocation}`);
    const shiftsQ = query(
      collection(db, `tenants/${tenantId}/shifts`),
      where("locationId", "==", selectedLocation),
      where("localDateKey", "==", currentDateKey)
    );
    const entriesQ = query(
      collection(db, `tenants/${tenantId}/timeEntries`),
      where("locationId", "==", selectedLocation),
      where("businessDateKey", "==", currentDateKey)
    );
    const legacyEntriesQ = query(
      collection(db, `tenants/${tenantId}/timeEntries`),
      where("locationId", "==", selectedLocation),
      where("date", "==", currentDateKey)
    );

    let latestTargets = null;
    let latestShifts = [];
    let latestEntriesById = new Map();
    let latestLegacyEntriesById = new Map();

    const recomputeRisk = () => {
      const dayparts = Array.isArray(latestTargets?.dayparts) ? latestTargets.dayparts : [];
      if (!dayparts.length) {
        setRiskAlerts([]);
        setRiskMeta({ enabled: true, hasTargets: false });
        return;
      }

      const localNow = DateTime.now().setZone(locationTimeZone);
      const currentMinute = localNow.hour * 60 + localNow.minute;
      const businessDay = DateTime.fromISO(currentDateKey, { zone: locationTimeZone });
      const dayKey = businessDay.isValid ? DAY_KEYS[businessDay.weekday % 7] : DAY_KEYS[localNow.weekday % 7];
      const targets = latestTargets?.targets || {};
      const useSameEveryDay = latestTargets?.useSameEveryDay !== false;

      const activeByEmployee = new Map();
      const combinedEntries = new Map([...latestEntriesById, ...latestLegacyEntriesById]);
      Array.from(combinedEntries.values()).forEach((entry) => {
        const employeeId = entry?.employeeId ? String(entry.employeeId) : "";
        if (!employeeId || entry?.status === "voided") return;
        const type = String(entry.type || "").toLowerCase();
        const ts = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp || 0);
        if (!(ts instanceof Date) || Number.isNaN(ts.getTime())) return;
        const existing = activeByEmployee.get(employeeId);
        if (!existing || ts > existing.ts) activeByEmployee.set(employeeId, { ts, type, entry });
      });

      const activeEmployees = new Map();
      activeByEmployee.forEach((v, employeeId) => {
        if (v.type === "clock_in" || v.type === "break_start" || v.type === "break_end") {
          activeEmployees.set(employeeId, v.entry || {});
        }
      });

      const shiftsByEmployee = new Map();
      latestShifts.forEach((shift) => {
        if (shift?.deleted === true) return;
        const employeeId = shift?.employeeId ? String(shift.employeeId) : "";
        if (!employeeId) return;
        const list = shiftsByEmployee.get(employeeId) || [];
        list.push(shift);
        shiftsByEmployee.set(employeeId, list);
      });

      const computed = [];
      for (const part of dayparts) {
        const partId = String(part?.id || "");
        const startMin = parseTimeToMinutes(part?.start);
        const endMin = parseTimeToMinutes(part?.end);
        if (!partId || startMin == null || endMin == null) continue;
        const daypartRange = expandRange(startMin, endMin);
        if (!daypartRange) continue;

        const targetCount = resolveDaypartTargetCount(targets, dayKey, partId, useSameEveryDay);
        const inDaypartNow =
          daypartRange.end > 1440
            ? (currentMinute >= daypartRange.start || currentMinute < (daypartRange.end - 1440))
            : (currentMinute >= daypartRange.start && currentMinute < daypartRange.end);

        let scheduledCount = 0;
        latestShifts.forEach((shift) => {
          if (shift?.deleted === true) return;
          if (!shift?.employeeId || shift?.isOpenShift === true) return;
          const s = parseTimeToMinutes(shift.shiftStartTime);
          const e = parseTimeToMinutes(shift.shiftEndTime);
          const shiftRange = expandRange(s, e);
          if (!shiftRange) return;
          if (rangesOverlap(shiftRange, daypartRange)) scheduledCount += 1;
        });

        let liveCount = 0;
        activeEmployees.forEach((entry, employeeId) => {
          const empShifts = shiftsByEmployee.get(employeeId) || [];
          let matched = false;
          for (const s of empShifts) {
            const ss = parseTimeToMinutes(s.shiftStartTime);
            const se = parseTimeToMinutes(s.shiftEndTime);
            const shiftRange = expandRange(ss, se);
            if (!shiftRange) continue;
            if (rangesOverlap(shiftRange, daypartRange)) {
              matched = true;
              break;
            }
          }
          if (matched || (!empShifts.length && inDaypartNow)) liveCount += 1;
        });

        const scheduledGap = scheduledCount - targetCount;
        const liveGap = liveCount - targetCount;
        const effectiveGap = inDaypartNow ? liveGap : scheduledGap;

        let severity = "safe";
        if (effectiveGap <= -2) severity = "critical";
        else if (effectiveGap === -1) severity = "watch";
        else if (effectiveGap >= 2) severity = "over";

        const delta = Math.abs(effectiveGap);
        const message =
          effectiveGap >= 2
            ? `Overstaffed by ${delta}`
            : effectiveGap <= -1
              ? `Understaffed by ${delta}`
              : "On target";

        computed.push({
          id: partId,
          severity,
          name: String(part?.name || partId),
          window: `${formatDisplayTime(part?.start)} - ${formatDisplayTime(part?.end)}`,
          message,
          targetCount,
          scheduledCount,
          liveCount,
          isLiveWindow: inDaypartNow,
        });
      }

      const severityOrder = { critical: 0, watch: 1, safe: 2, over: 3 };
      computed.sort((a, b) => {
        const sev = severityOrder[a.severity] - severityOrder[b.severity];
        if (sev !== 0) return sev;
        return Math.abs(b.targetCount - (b.isLiveWindow ? b.liveCount : b.scheduledCount)) -
          Math.abs(a.targetCount - (a.isLiveWindow ? a.liveCount : a.scheduledCount));
      });

      setRiskAlerts(computed);
      setRiskMeta({ enabled: true, hasTargets: true });
    };

    const unsubs = [];
    unsubs.push(onSnapshot(staffingRef, (snap) => {
      latestTargets = snap.exists() ? snap.data() : null;
      recomputeRisk();
    }));
    unsubs.push(onSnapshot(shiftsQ, (snap) => {
      latestShifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      recomputeRisk();
    }));

    const refreshEntries = (snap) => {
      latestEntriesById = new Map();
      snap.docs.forEach((d) => latestEntriesById.set(d.id, d.data()));
      recomputeRisk();
    };
    const refreshLegacyEntries = (snap) => {
      latestLegacyEntriesById = new Map();
      snap.docs.forEach((d) => latestLegacyEntriesById.set(d.id, d.data()));
      recomputeRisk();
    };
    unsubs.push(onSnapshot(entriesQ, refreshEntries));
    unsubs.push(onSnapshot(legacyEntriesQ, refreshLegacyEntries));

    return () => unsubs.forEach((u) => u && u());
  }, [tenantId, selectedLocation, dateMode, currentDateKey, locationTimeZone]);

  const dashboardMatches =
    !!dashboardData &&
    dateMode === "today" &&
    dashboardData.locationId === selectedLocation &&
    dashboardData.localDateKey === currentDateKey;
  const dashboardByQueryMatches =
    !!dashboardDataByQuery &&
    dateMode === "today" &&
    dashboardDataByQuery.locationId === selectedLocation &&
    dashboardDataByQuery.localDateKey === currentDateKey;
  const liveMatches =
    !!liveMetrics &&
    liveMetrics.locationId === selectedLocation &&
    liveMetrics.mode === dateMode &&
    (dateMode !== "today" || liveMetrics.localDateKey === currentDateKey);
  const effectiveData = dashboardMatches
    ? { ...(liveMatches ? liveMetrics : {}), ...dashboardData }
    : liveMatches
      ? liveMetrics
      : null;
  const laborSource = dateMode === "today"
    ? (dashboardMatches ? dashboardData : (dashboardByQueryMatches ? dashboardDataByQuery : null))
    : null;
  const laborData = { ...(effectiveData || {}), ...(laborSource || {}) };
  const plannedLaborPercent = Number(laborData?.plannedLaborPercent || 0);
  const actualLaborPercent = Number(laborData?.actualLaborPercent || 0);
  const variancePercent = actualLaborPercent - plannedLaborPercent;
  const varianceIsFavorable = variancePercent <= 0;
  const safeCoverageThresholdPctRaw =
    selectedLocationObj?.labor?.minimumSafeCoveragePct ??
    selectedLocationObj?.labor?.safeCoverageThresholdPct ??
    selectedLocationObj?.labor?.staffingSafeCoveragePct ??
    selectedLocationObj?.minimumSafeCoveragePct;
  const safeCoverageThresholdPct = clamp(Number(safeCoverageThresholdPctRaw || 80), 1, 100);
  const coverageWarningBandPct = 10;
  const currencyFmt = (val) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
      .format(Number(val || 0));
  const percentFmt = (val) => `${Number(val || 0).toFixed(1)}%`;
  const hourFmt = (val) => `${Number(val || 0).toFixed(1)}h`;
  const overtimeRiskRows = useMemo(() => {
    if (!otRiskData || !Array.isArray(otRiskData.employees)) return [];
    const inCurrentWeek = otRiskData.weekStartKey && otRiskData.weekEndKey
      ? currentDateKey >= String(otRiskData.weekStartKey) && currentDateKey <= String(otRiskData.weekEndKey)
      : true;
    if (!inCurrentWeek) return [];
    return otRiskData.employees;
  }, [otRiskData, currentDateKey]);
  const snapshotCards = effectiveData
    ? [
        {
          key: "scheduledHours",
          label: "Scheduled Hours",
          value: Number(effectiveData.scheduledHours || 0).toFixed(1),
          accent: "#1565c0",
          glow: "rgba(21, 101, 192, 0.14)",
        },
        {
          key: "openShifts",
          label: "Open Shifts",
          value: Number(effectiveData.openShifts || 0),
          accent: "#6d4c41",
          glow: "rgba(109, 76, 65, 0.14)",
        },
        {
          key: "lateCount",
          label: "Late Employees",
          value: Number(effectiveData.lateCount || 0),
          accent: "#c62828",
          glow: "rgba(198, 40, 40, 0.14)",
        },
        {
          key: "clockedInCount",
          label: "Employees Clocked In",
          value: Number(effectiveData.clockedInCount || 0),
          accent: "#2e7d32",
          glow: "rgba(46, 125, 50, 0.14)",
        },
        {
          key: "earlyOutCount",
          label: "Early Out Employees",
          value: Number(effectiveData.earlyOutCount || 0),
          accent: "#7b1fa2",
          glow: "rgba(123, 31, 162, 0.14)",
        },
      ]
    : [];

  const renderCoverageCard = (alert, { isPrimary = false } = {}) => {
    const targetCount = Number(alert.targetCount || 0);
    const scheduledCount = Number(alert.scheduledCount || 0);
    const coveragePctRaw = targetCount > 0 ? (scheduledCount / targetCount) * 100 : 100;
    const coveragePct = clamp(coveragePctRaw, 0, 120);
    const gaugePct = clamp(coveragePctRaw, 0, 100);
    const severity = resolveCoverageSeverity(coveragePctRaw, safeCoverageThresholdPct, coverageWarningBandPct);
    const safeHeadcount = Math.ceil((targetCount * safeCoverageThresholdPct) / 100);
    const neededForSafe = Math.max(0, safeHeadcount - scheduledCount);
    const arcLength = Math.PI * 44;
    const filledArc = (gaugePct / 100) * arcLength;
    const operationalRed = "#e5533d";
    const gaugeColor =
      severity === "safe" ? "#2e7d32" : severity === "strain" ? "#ef6c00" : operationalRed;
    const cardBorder =
      severity === "safe"
        ? "rgba(46,125,50,0.34)"
        : severity === "strain"
          ? "rgba(245,124,0,0.34)"
          : "rgba(198,40,40,0.35)";
    const cardBackground =
      severity === "safe"
        ? "linear-gradient(180deg, rgba(232,245,233,0.78) 0%, rgba(255,255,255,0.98) 100%)"
        : severity === "strain"
          ? "linear-gradient(180deg, rgba(255,243,224,0.78) 0%, rgba(255,255,255,0.98) 100%)"
          : "linear-gradient(180deg, rgba(255,235,238,0.78) 0%, rgba(255,255,255,0.98) 100%)";
    const riskStateLabel =
      severity === "critical" ? "CRITICAL COVERAGE" : severity === "strain" ? "STRAINED COVERAGE" : "SAFE COVERAGE";
    const riskStateColor =
      severity === "critical" ? operationalRed : severity === "strain" ? "#fb8c00" : "#43a047";
    const stabilizationText =
      neededForSafe > 0 ? `Needs +${neededForSafe} staff to stabilize` : "Coverage is stable";
    const compactWindow = compactWindowLabel(alert.window);

    return (
      <Card
        key={alert.id}
        sx={{
          borderRadius: 2,
          minHeight: isPrimary ? { xs: 155, sm: 172 } : undefined,
          border: "1px solid",
          borderWidth: isPrimary ? "2px" : "1px",
          borderColor: isPrimary ? "rgba(229,83,61,0.45)" : cardBorder,
          background: cardBackground,
          boxShadow: isPrimary
            ? "0 8px 20px rgba(15,23,42,0.12), 0 0 0 1px rgba(229,83,61,0.14)"
            : undefined,
          position: "relative",
          overflow: "hidden",
          "&::before": isPrimary
            ? {
                content: "\"\"",
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "7px",
                background: "linear-gradient(180deg, rgba(229,83,61,0.92) 0%, rgba(229,83,61,0.62) 100%)",
              }
            : undefined,
        }}
      >
        <CardContent sx={{ p: isPrimary ? 1.05 : 1 }}>
          <Box sx={{ display: "grid", gap: isPrimary ? 0.55 : 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                {isPrimary ? (
                  <Chip
                    size="small"
                    label="Primary Risk"
                    sx={{
                      height: 20,
                      borderRadius: 1.2,
                      background: "rgba(229,83,61,0.12)",
                      color: "#9f2f20",
                      "& .MuiChip-label": { px: 0.75, fontSize: "0.64rem", fontWeight: 700, letterSpacing: 0.35, textTransform: "uppercase" },
                    }}
                  />
                ) : null}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.55, flexWrap: "wrap" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.2, color: "text.primary" }}>
                    {String(alert.name || "").toUpperCase()}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.86rem", color: "text.secondary" }}>
                    {compactWindow}
                  </Typography>
                </Box>
              </Box>
              {alert.isLiveWindow ? (
                <Chip
                  size="small"
                  label="Live"
                  sx={{
                    height: 20,
                    borderRadius: 1.2,
                    background: isPrimary ? "rgba(229,83,61,0.14)" : "rgba(198,40,40,0.14)",
                    color: isPrimary ? "#a53a2a" : "error.dark",
                    "& .MuiChip-label": { px: 0.8, fontSize: "0.65rem", fontWeight: 700, letterSpacing: 0.3 },
                  }}
                />
              ) : null}
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 0.45,
                justifyItems: "center",
                width: isPrimary ? { xs: "100%", sm: 260 } : "100%",
                mx: "auto",
                pl: isPrimary ? { sm: 2.5 } : 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: riskStateColor,
                  fontWeight: 800,
                  letterSpacing: 0.65,
                  textTransform: "uppercase",
                  lineHeight: 1.1,
                }}
              >
                {riskStateLabel}
              </Typography>

              <Box sx={{ position: "relative", width: isPrimary ? 158 : 142, height: isPrimary ? 94 : 84 }}>
                <svg width={isPrimary ? "158" : "142"} height={isPrimary ? "94" : "84"} viewBox="0 0 120 72" aria-hidden="true">
                  <path
                    d="M 16 60 A 44 44 0 0 1 104 60"
                    fill="none"
                    stroke="rgba(15,23,42,0.12)"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 16 60 A 44 44 0 0 1 104 60"
                    fill="none"
                    stroke={gaugeColor}
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${filledArc} ${arcLength}`}
                  />
                </svg>
                <Typography
                  variant="h5"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 34,
                    transform: "translateX(-50%)",
                    fontWeight: 800,
                    color: gaugeColor,
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {Math.round(coveragePct)}%
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 56,
                    transform: "translateX(-50%)",
                    fontWeight: 700,
                    color: "text.secondary",
                  }}
                >
                  Coverage
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  textAlign: "center",
                  color: severity === "critical" ? "#b23f2c" : severity === "strain" ? "warning.dark" : "text.primary",
                }}
              >
                {stabilizationText}
              </Typography>
              <Box sx={{ pt: 0.15, display: "flex", justifyContent: "center" }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: isPrimary ? "rgba(229,83,61,0.5)" : undefined,
                    color: isPrimary ? "#b23f2c" : undefined,
                  }}
                >
                  Find Coverage
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#f8fafb", py: 1.5 }}>
      <Container
        maxWidth="xl"
        sx={{
          mt: 1,
          "& .MuiCard-root": {
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
          },
        }}
      >
      <Box sx={{ mb: 1.8 }}>
        <Typography
          variant="overline"
          sx={{
            display: "block",
            letterSpacing: 2.2,
            fontWeight: 800,
            color: "rgba(15, 23, 42, 0.55)",
            lineHeight: 1,
            mb: 0.35,
          }}
        >
          OPERATIONS
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
            color: "#0f172a",
            textShadow: "0 1px 0 rgba(255,255,255,0.7)",
          }}
        >
          Manager Dashboard
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControl
            sx={{
              minWidth: 280,
              "& .MuiInputLabel-root": {
                fontWeight: 700,
                letterSpacing: 0.35,
                color: "rgba(15,23,42,0.66)",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.2,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.09)",
                "& fieldset": {
                  borderColor: "rgba(15,23,42,0.16)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(2,136,209,0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#0288d1",
                  borderWidth: "1px",
                },
              },
              "& .MuiSelect-select": {
                fontWeight: 600,
                letterSpacing: 0.15,
                color: "#0f172a",
              },
            }}
            size="small"
          >
            <InputLabel id="dashboard-location-label">Location</InputLabel>
            <Select
              labelId="dashboard-location-label"
              id="dashboard-location-select"
              value={selectedLocation}
              label="Location"
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name || loc.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={dateMode}
            exclusive
            size="small"
            onChange={(_, value) => value && setDateMode(value)}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            px: 1.4,
            py: 0.65,
            borderRadius: "999px",
            border: "1px solid rgba(15, 23, 42, 0.12)",
            background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
            boxShadow: "0 6px 14px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Typography variant="caption" sx={{ letterSpacing: 0.8, color: "text.secondary", fontWeight: 700 }}>
            LAST UPDATED
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.1, fontWeight: 600, color: "text.primary" }}>
            {formatLastUpdated(lastUpdatedAt)}
          </Typography>
        </Box>
      </Box>

      {selectedLocation && selectedLocation !== "all" && businessDayLabel && (
        <Box
          sx={{
            mb: 0.85,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.2,
            py: 0.55,
            borderRadius: 999,
            border: "1px solid rgba(2, 136, 209, 0.25)",
            background: "linear-gradient(180deg, rgba(2,136,209,0.08) 0%, rgba(255,255,255,0.9) 100%)",
          }}
        >
          <Typography variant="caption" sx={{ letterSpacing: 0.8, fontWeight: 800, color: "info.dark" }}>
            BUSINESS DAY
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
            {businessDayLabel.replace(/^Business Day:\s*/i, "")}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 1.7, fontWeight: 800, color: "text.primary", lineHeight: 1 }}
        >
          {dateMode === "today" ? "TODAY SNAPSHOT" : "WEEK SNAPSHOT"}
        </Typography>
        <Box sx={{ height: 1, flex: 1, background: "linear-gradient(90deg, rgba(15,23,42,0.25), rgba(15,23,42,0.05))" }} />
      </Box>

      {!selectedLocation && (
        <Typography>Select a location to view metrics.</Typography>
      )}

      {selectedLocation && !effectiveData && (
        <Typography>No data for today.</Typography>
      )}

      {effectiveData && (
        <>
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            {snapshotCards.map((card) => (
              <Card
                key={card.key}
                sx={{
                  borderRadius: 2.5,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  background: `radial-gradient(circle at 88% 14%, ${card.glow} 0%, rgba(255,255,255,0.98) 52%)`,
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ height: 4, backgroundColor: card.accent }} />
                <CardContent sx={{ py: 1.25, px: 1.6 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: 1.1,
                      color: card.accent,
                      fontWeight: 700,
                      display: "block",
                      lineHeight: 1.2,
                      mb: 0.35,
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.05 }}>
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box
            sx={{
              mt: 2,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 3,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background:
                "linear-gradient(180deg, rgba(2, 136, 209, 0.03) 0%, rgba(46, 125, 50, 0.03) 100%)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 1.8, fontWeight: 800, color: "text.primary", lineHeight: 1 }}
              >
                LABOR PERFORMANCE
              </Typography>
              <Box sx={{ height: 1, flex: 1, background: "linear-gradient(90deg, rgba(15,23,42,0.22), rgba(15,23,42,0.04))" }} />
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(2, 136, 209, 0.45)",
                  background:
                    "linear-gradient(140deg, rgba(2, 136, 209, 0.12) 0%, rgba(255, 255, 255, 0.95) 55%)",
                  boxShadow: "0 8px 24px rgba(2, 136, 209, 0.12)",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 1.75 } }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.25, color: "info.dark", fontWeight: 700 }}>
                    PLANNED
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <Typography variant="body1" color="text.secondary">Forecasted Sales</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                      {currencyFmt(laborData.forecastedSales)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1, borderColor: "rgba(2, 136, 209, 0.18)" }} />
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">Scheduled Labor $</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 650, letterSpacing: "-0.01em" }}>
                      {currencyFmt(laborData.scheduledLaborCost)}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 0.75, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">Planned Labor %</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 650, letterSpacing: "-0.01em" }}>
                      {percentFmt(plannedLaborPercent)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(46, 125, 50, 0.45)",
                  background:
                    "linear-gradient(140deg, rgba(46, 125, 50, 0.12) 0%, rgba(255, 255, 255, 0.95) 55%)",
                  boxShadow: "0 8px 24px rgba(46, 125, 50, 0.12)",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 1.75 } }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.25, color: "success.dark", fontWeight: 700 }}>
                    ACTUAL
                  </Typography>
                  <Box sx={{ mt: 0.75 }}>
                    <Typography variant="body1" color="text.secondary">Actual Sales</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                      {currencyFmt(laborData.actualSales)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1, borderColor: "rgba(46, 125, 50, 0.2)" }} />
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">Actual Labor $</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 650, letterSpacing: "-0.01em" }}>
                      {currencyFmt(laborData.actualLaborCost)}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 0.75, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">Actual Labor %</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 650, letterSpacing: "-0.01em" }}>
                      {percentFmt(actualLaborPercent)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}>
              <Chip
                label={`Variance ${variancePercent > 0 ? "+" : ""}${percentFmt(variancePercent)}`}
                sx={{
                  px: 1.4,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  height: 40,
                  borderRadius: "999px",
                  letterSpacing: 0.2,
                  border: "1px solid",
                  borderColor: varianceIsFavorable ? "rgba(46, 125, 50, 0.35)" : "rgba(211, 47, 47, 0.35)",
                  color: varianceIsFavorable ? "success.dark" : "error.dark",
                  background: varianceIsFavorable
                    ? "linear-gradient(180deg, rgba(46, 125, 50, 0.2) 0%, rgba(255, 255, 255, 0.92) 100%)"
                    : "linear-gradient(180deg, rgba(211, 47, 47, 0.2) 0%, rgba(255, 255, 255, 0.92) 100%)",
                  boxShadow: varianceIsFavorable
                    ? "0 8px 20px rgba(46, 125, 50, 0.15)"
                    : "0 8px 20px rgba(211, 47, 47, 0.15)",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1.2,
              p: { xs: 1, sm: 1.2 },
              borderRadius: 3,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background:
                "linear-gradient(180deg, rgba(255, 244, 229, 0.25) 0%, rgba(255, 255, 255, 0.95) 100%)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: 1.2,
                gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
                alignItems: "start",
                minHeight: { md: "min(72vh, 780px)" },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 0.8,
                  minHeight: { md: 0 },
                  overflowY: { md: "auto" },
                  pr: { md: 0.2 },
                }}
              >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      px: 0.85,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: "1px solid rgba(15,23,42,0.08)",
                      background: "linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.96) 100%)",
                    }}
                  >
                    <FlagOutlinedIcon sx={{ fontSize: 15, color: "rgba(15,23,42,0.72)" }} />
                    <Typography variant="overline" sx={{ letterSpacing: 1.2, fontWeight: 800, color: "rgba(15,23,42,0.88)", lineHeight: 1 }}>
                      TODAY'S STAFFING TARGETS
                    </Typography>
                    <Box sx={{ height: 1, flex: 1, background: "linear-gradient(90deg, rgba(15,23,42,0.22), rgba(15,23,42,0.04))" }} />
                  </Box>

                  {!riskMeta.enabled || !riskMeta.hasTargets ? (
                    <Typography variant="body2" color="text.secondary">
                      No staffing targets configured for this location/day.
                    </Typography>
                  ) : !riskAlerts.length ? (
                    <Typography variant="body2" color="text.secondary">
                      No active staffing risk alerts for the current business day.
                    </Typography>
                  ) : (
                    (() => {
                      const [primaryAlert, ...secondaryAlerts] = riskAlerts;
                      return (
                        <Box sx={{ display: "grid", gap: 0.75 }}>
                          {primaryAlert ? renderCoverageCard(primaryAlert, { isPrimary: true }) : null}
                          {secondaryAlerts.length ? (
                            <Box sx={{ display: "grid", gap: 0.75, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}>
                              {secondaryAlerts.map((alert) => renderCoverageCard(alert))}
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })()
                  )}
                </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 0.8,
                  minHeight: { md: 0 },
                  overflowY: { md: "auto" },
                  pr: { md: 0.1 },
                }}
              >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                      px: 0.85,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: "1px solid rgba(15,23,42,0.08)",
                      background: "linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.96) 100%)",
                      flexWrap: "wrap",
                    }}
                  >
                    <InsightsOutlinedIcon sx={{ fontSize: 15, color: "rgba(15,23,42,0.72)" }} />
                    <Typography variant="overline" sx={{ letterSpacing: 1.2, fontWeight: 800, color: "rgba(15,23,42,0.88)", lineHeight: 1 }}>
                      OVERTIME RISK & AUTO CLOCKOUTS
                    </Typography>
                    <Box sx={{ height: 1, flex: 1, minWidth: 20, background: "linear-gradient(90deg, rgba(15,23,42,0.22), rgba(15,23,42,0.04))" }} />
                  </Box>

                  <Card
                    sx={{
                      borderRadius: 1.9,
                      border: "1px solid rgba(2,136,209,0.24)",
                      background: "linear-gradient(180deg, rgba(227,242,253,0.5) 0%, rgba(255,255,255,0.97) 100%)",
                    }}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.85, fontWeight: 800, color: "info.dark" }}>
                        Auto Clock-Outs (7 Days)
                      </Typography>
                      {!autoClockOutRows.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                          No auto clock-outs in the last 7 days.
                        </Typography>
                      ) : (
                        <Box sx={{ mt: 0.5, display: "grid", gap: 0.6 }}>
                          {autoClockOutRows.slice(0, 3).map((row) => (
                            <Box key={`auto-clockout-${row.employeeId}-${row.clockOutAt?.getTime?.() || ""}`}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
                                {row.employeeName} auto clocked out at {formatClockTimeLabel(row.clockOutAt, locationTimeZone)}
                              </Typography>
                              {row.reason ? (
                                <Typography variant="caption" color="text.secondary">
                                  {row.reason}
                                </Typography>
                              ) : null}
                            </Box>
                          ))}
                          <Box sx={{ mt: 0.1, display: "flex", gap: 0.6, flexWrap: "wrap" }}>
                            <Button size="small" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
                              Edit Time
                            </Button>
                            <Button size="small" variant="text" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
                              Contact Employee
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {!otRiskData ? (
                    <Typography variant="caption" color="text.secondary">
                      Overtime snapshot pending. Create/edit a shift or time entry to trigger recalculation.
                    </Typography>
                  ) : otRiskData.weeklyOvertimeEnabled === false ? (
                    <Typography variant="caption" color="text.secondary">
                      Weekly overtime monitoring is not enabled for this location.
                    </Typography>
                  ) : !overtimeRiskRows.length ? (
                    <Card
                      sx={{
                        borderRadius: 1.9,
                        border: "1px solid rgba(46,125,50,0.26)",
                        background: "linear-gradient(180deg, rgba(232,245,233,0.7) 0%, rgba(255,255,255,0.98) 100%)",
                      }}
                    >
                      <CardContent sx={{ p: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.85,
                            fontWeight: 800,
                            color: "success.dark",
                          }}
                        >
                          Overtime Status
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.2, fontWeight: 700, color: "text.primary" }}>
                          Great news: no overtime-risk employees this business week.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.1, display: "block" }}>
                          Team hours are currently tracking within weekly thresholds.
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box sx={{ display: "grid", gap: 0.75 }}>
                      <Card
                        sx={{
                          borderRadius: 1.9,
                          border: "1px solid rgba(211,47,47,0.22)",
                          background: "linear-gradient(180deg, rgba(255,235,238,0.64) 0%, rgba(255,255,255,0.96) 100%)",
                        }}
                      >
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.85, fontWeight: 800, color: "error.dark" }}>
                            {`Payroll Risk - ${
                              otRiskData?.weekStartKey && otRiskData?.weekEndKey
                                ? formatWeekRangeLabel(otRiskData.weekStartKey, otRiskData.weekEndKey)
                                : "This Week"
                            }`}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.2, fontWeight: 700, color: "text.primary" }}>
                            Projected overtime cost: {currencyFmt(otRiskData?.summary?.estimatedExtraLaborCost || 0)}
                          </Typography>
                        </CardContent>
                      </Card>

                      {overtimeRiskRows.map((row) => (
                        <Card
                          key={row.employeeId}
                          sx={{
                            borderRadius: 1.8,
                            border: "1px solid",
                            borderColor:
                              row.severity === "critical"
                                ? "rgba(198,40,40,0.35)"
                                : row.severity === "watch"
                                  ? "rgba(245,124,0,0.35)"
                                  : "rgba(2,136,209,0.3)",
                            background:
                              row.severity === "critical"
                                ? "linear-gradient(180deg, rgba(255,235,238,0.72) 0%, rgba(255,255,255,0.98) 100%)"
                                : row.severity === "watch"
                                  ? "linear-gradient(180deg, rgba(255,243,224,0.72) 0%, rgba(255,255,255,0.98) 100%)"
                                  : "linear-gradient(180deg, rgba(227,242,253,0.68) 0%, rgba(255,255,255,0.98) 100%)",
                          }}
                        >
                          <CardContent sx={{ p: 1, display: "grid", gap: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                {row.employeeName || row.employeeId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.7, fontWeight: 700 }}>
                                {row.currentlyClockedIn ? "Live" : "Scheduled"}
                              </Typography>
                            </Box>

                            <Box sx={{ height: 9, borderRadius: 999, background: "rgba(15,23,42,0.1)", overflow: "hidden" }}>
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${burnPercent(row.projectedWeekHours, row.thresholdHours)}%`,
                                  borderRadius: 999,
                                  background:
                                    row.overtimeRiskHours > 0
                                      ? "linear-gradient(90deg, #ef5350 0%, #c62828 100%)"
                                      : "linear-gradient(90deg, #f57c00 0%, #ef6c00 100%)",
                                }}
                              />
                            </Box>

                            <Typography variant="caption" color="text.secondary">
                              {Number(row.projectedWeekHours || 0).toFixed(1)} hrs (Over by {Math.max(0, Number(row.overtimeRiskHours || 0)).toFixed(1)})
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: row.overtimeRiskHours > 0 ? "error.main" : "warning.dark",
                                letterSpacing: 0.2,
                              }}
                            >
                              {resolveOvertimeHitLabel(row)}
                            </Typography>

                            <Box sx={{ pt: 0.1 }}>
                              <Button size="small" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
                                Adjust Schedule
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
          </Box>
        </>
      )}
      </Container>
    </Box>
  );
}
